import * as React from 'react'
import {
  RequestContext,
  GetStringsInput,
  Logger,
  buildDvString
} from 'DVWebNodeUtils'
import { Text } from '@amzn/dvui/dist/Text'
import { strings as productMetaInfoString } from '../productMetaInfo/strings'
import { strings as morePurchaseOptionsString } from '../morePurchaseOptions/strings'
import { strings as refundString } from '../refund/strings'
import { strings as episodeString } from '../episode/strings'
import { strings as redeemAndPromoLinkString } from '../redeemAndPromoLink/strings'
import { strings as customerErrorMessageStrings } from '../customerErrorMessage/strings'
import { strings as scheduleStrings } from '../schedule/strings'
import { strings as seasonSelectorStrings } from '../seasonSelector/strings'
import { strings as badgeStrings } from '../badges/strings'
import { strings as watchlistStrings } from '../watchlist/strings'
import { strings as feedbackFormStrings } from '../feedbackForm/strings'
import { strings as synopsisStrings } from '../synopsis/strings'
import { strings as pageUnavailableStrings } from '../pageUnavailable/strings'
import { strings as paginationStrings } from '../collection/strings'
import { strings as playbackMessagingStrings } from '../playbackMessaging/strings'
import { strings as actionBoxStrings } from '../actionBox/strings'
import { strings as castAndCrewStrings } from '../castAndCrew/strings'
import { strings as playerStrings } from '../player/strings'
import { strings as otherFormatsStrings } from '../otherFormats/strings'
import { strings as episodeListStrings } from '../episodeList/strings'
import { strings as giftCardStrings } from '../giftCard/strings'
import { strings as tabsStrings } from '../tabs/strings'
import { strings as watchPartyLinkStrings } from '../watchPartyLink/strings'
import { strings as bannerStrings } from '../uiBanner/strings'
import { strings as playbackStrings } from '../playback/strings'

import { DecodedText } from '../decodedText/decodedText'
import { DVStringAttrs } from '../../utils/stringUtils'
import { StringBundle } from './stringBundle'
import { DpWidgetContext } from '../../utils/dpWidgetContext'

export interface ITextLocalizerProps {
  stringID: StringId
  replace?: { [key: string]: string }
  dvStringAttrs?: DVStringAttrs
  shouldDecode?: boolean
  logger: Logger
}

const allStrings = [
  productMetaInfoString,
  morePurchaseOptionsString,
  episodeString,
  redeemAndPromoLinkString,
  customerErrorMessageStrings,
  scheduleStrings,
  paginationStrings,
  actionBoxStrings,
  castAndCrewStrings,
  otherFormatsStrings,
  episodeListStrings,
  giftCardStrings,
  tabsStrings,
  watchPartyLinkStrings,
  seasonSelectorStrings,
  badgeStrings,
  watchlistStrings,
  synopsisStrings,
  refundString,
  pageUnavailableStrings,
  playbackMessagingStrings,
  playerStrings,
  feedbackFormStrings,
  bannerStrings,
  playbackStrings,
]

export type StringId = (typeof allStrings[number])['strings'][number]

export const getStringIds = (bundleName: StringBundle): string[] =>
  ([] as StringId[]).concat(
    ...allStrings
      .filter((s) => s.bundles.includes(bundleName))
      .map((s) => s.strings)
  )

export const getStringsInput = (
  context: RequestContext,
  bundleName: StringBundle
) => ({ context, ids: getStringIds(bundleName) } as GetStringsInput)

type TextLocalizerProps = {
  stringID: StringId
  replace?: { [key: string]: string }
  dvStringAttrs?: DVStringAttrs
  shouldDecode?: boolean
  dir?: string
}


export const TextLocalizer = ({
  stringID,
  replace,
  dvStringAttrs,
  shouldDecode = false,
  dir,
}: TextLocalizerProps) => {
  const { getString } = React.useContext(DpWidgetContext)
  const string = getString(stringID, replace)
  const dvStringTuple = buildDvString(string, dvStringAttrs || {})
  const content = shouldDecode ? (
    <DecodedText string={dvStringTuple.string} />
  ) : (
    <Text string={dvStringTuple} />
  )
  return dir ? <span dir={dir}> {content} </span> : content
}
